<style lang="scss" scoped>
$font:       'Segoe UI', sans-serif;
$blue:       #389ad4;
$dark:       #0d1b3e;
$green:      #276749;
$red:        #e53e3e;
$border:     #dde2ec;
$def-border: #c5d4f0;

.page-wrap {
  padding: 8px 0 90px;
  font-family: $font;
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: hidden;
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;
  gap: 12px;
  flex-wrap: wrap;

  .header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: $blue;
    display: flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
  }

  .loc-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1px solid $def-border;
    color: $dark;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;

    i { font-size: 11px; color: $blue; }
  }

  .sel-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #eef7fb;
    border: 1px solid #bee3f8;
    color: #185FA5;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
  }

}

/* ── Summary bar ── */
.summary-bar {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #f0f3fa;

  .sb-stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 10px;
    gap: 3px;

    .sb-val {
      font-size: 20px;
      font-weight: 700;
      color: $dark;
      line-height: 1;

      &.configured   { color: $green; }
      &.unconfigured { color: #a0aab8; }
    }

    .sb-label {
      font-size: 10.5px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: #a0aab8;
    }
  }

  .sb-divider {
    width: 1px;
    background: #f0f3fa;
    margin: 10px 0;
  }
}

/* ── List column headers ── */
.list-header {
  display: grid;
  grid-template-columns: 36px 1fr 120px 120px 120px 44px;
  align-items: center;
  padding: 8px 16px;
  background: #fafbfd;
  border-bottom: 2px solid #eef0f8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #a0aab8;
  gap: 8px;
}

/* ── Service row ── */
.svc-row {
  border-bottom: 1px solid #f5f7fa;
  transition: background 0.12s;

  &:last-child { border-bottom: none; }

  &.is-checked > .svc-row-head {
    background: #f0f8ff;
    border-left: 3px solid $blue;
  }

  &.is-expanded > .svc-row-head {
    background: #f8fbff;
    border-bottom: 1px solid #e8edf5;
  }
}

.svc-row-head {
  display: grid;
  grid-template-columns: 36px 1fr 120px 120px 120px 44px;
  align-items: center;
  padding: 11px 16px;
  cursor: pointer;
  gap: 8px;
  transition: background 0.12s;
  border-left: 3px solid transparent;

  &:hover { background: #f8fbff; }
}

.srh-service {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;

  .svc-icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #eef2fb;
    color: #185FA5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
  }

  .svc-name {
    font-size: 13px;
    font-weight: 400;
    color: $dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.srh-rate {
  font-size: 13px;
  font-weight: 600;
  font-family: monospace;
  color: #4a5568;
  text-align: right;

  &.na { color: #c0c7d4; font-weight: 400; }

  .custom-rate-val { color: $green; }
  .na { color: #c0c7d4; font-weight: 400; }
}

.srh-status { text-align: center; }

.srh-toggle {
  display: flex;
  justify-content: center;

  .expand-icon {
    font-size: 11px;
    color: #a0aab8;
    transition: transform 0.2s;

    &.rotated { transform: rotate(180deg); }
  }
}

/* ── Status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &.s-custom  { background: #dcfce7; color: $green; }
  &.s-default { background: #e2e8f0; color: #718096; }
  &.s-fdle    { background: #fef9c3; color: #854d0e; }
}

/* ── Expanded detail panel ── */
.svc-detail {
  background: #f9fbfd;
  border-top: 1px solid #eef0f8;
  padding: 16px 20px 16px 56px;
}

.rate-compare {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  overflow: hidden;
  max-width: 640px;
}

.rc-header {
  display: grid;
  grid-template-columns: 1fr 140px 180px;
  padding: 8px 16px;
  background: #f5f8fc;
  border-bottom: 1px solid #e8edf5;
  gap: 12px;

  .rc-col {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #a0aab8;
  }

  .rc-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;

    &.def { background: #dbeafe; color: #1d4ed8; }
    &.cus { background: #dcfce7; color: $green; }
  }
}

.rc-row {
  display: grid;
  grid-template-columns: 1fr 140px 180px;
  align-items: center;
  padding: 9px 16px;
  border-bottom: 1px solid #f5f7fa;
  gap: 12px;

  &:last-child { border-bottom: none; }

  &:hover { background: #fafcff; }

  .rc-field {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
  }

  .rc-def-val {
    font-size: 12px;
    font-family: monospace;
    font-weight: 600;
    color: #7a8499;
    padding: 4px 10px;
    background: #f4f8fe;
    border-radius: 5px;
    display: inline-block;
  }
}

/* ── Rate input with prefix ── */
.input-wrap {
  display: flex;
  align-items: center;
  height: 30px;
  border: 1.5px solid $border;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: $blue;
    box-shadow: 0 0 0 2px rgba(56,154,212,0.1);
  }

  .input-prefix {
    height: 100%;
    padding: 0 6px 0 8px;
    background: #f5f7fa;
    border-right: 1px solid #e2e8f0;
    color: #a0aab8;
    font-size: 11px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    user-select: none;
    font-weight: 600;
  }

  .rate-input {
    flex: 1;
    border: none;
    outline: none;
    height: 100%;
    padding: 0 8px;
    font-size: 12.5px;
    color: $dark;
    font-family: $font;
    background: transparent;
    text-align: right;
    min-width: 0;

    &[type=number] { -moz-appearance: textfield; appearance: textfield; }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; appearance: none; margin: 0; }
  }
}

/* ── Toggle ── */
.toggle {
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  input { display: none; }

  .toggle-track {
    width: 32px;
    height: 17px;
    background: #e2e8f0;
    border-radius: 9px;
    position: relative;
    transition: background 0.2s;

    &::after {
      content: '';
      position: absolute;
      top: 2.5px;
      left: 2.5px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      transition: transform 0.2s;
    }
  }

  input:checked + .toggle-track {
    background: $red;
    &::after { transform: translateX(15px); }
  }
}

/* ── Checkbox ── */
.row-checkbox {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: $blue;
}

/* ── Error banner ── */
.error-banner {
  margin: 12px 20px;
  padding: 10px 14px;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  color: #c62828;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Empty / loading ── */
.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;

  i { font-size: 30px; display: block; margin-bottom: 10px; opacity: 0.45; }
}

/* ── Floating save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  z-index: 200;

  .save-bar__inner {
    background: $dark;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(13, 27, 62, 0.3);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .save-bar__info {
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      font-family: $font;

      strong { color: #fff; }
    }

    .btn-save {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      height: 36px;
      padding: 0 22px;
      background: $green;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: $font;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover { opacity: 0.9; }
      &:disabled { opacity: 0.55; cursor: not-allowed; }
    }
  }
}

</style>

<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="header-left">
          <span class="panel-title">
            <i class="fa fa-dollar-sign"></i>
            Rate Configuration
          </span>
          <span class="loc-chip" v-if="client.name">
            <i class="fa fa-map-marker-alt"></i>
            {{ client.name }}
          </span>
          <span class="sel-chip" v-if="selectedCount > 0">
            <i class="fa fa-check-square"></i>
            {{ selectedCount }} selected
          </span>
        </div>
      </div>

      <!-- Error banner -->
      <div class="error-banner" v-if="serviceError">
        <i class="fa fa-exclamation-circle"></i> {{ serviceError }}
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <i class="fa fa-spinner fa-spin"></i>
        Loading rate data…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="!loadingData && table_datas.length === 0">
        <i class="fa fa-dollar-sign"></i>
        No services found
      </div>

      <template v-else>
        <!-- Summary bar -->
        <div class="summary-bar">
          <div class="sb-stat">
            <span class="sb-val">{{ table_datas.length }}</span>
            <span class="sb-label">Total</span>
          </div>
          <div class="sb-divider"></div>
          <div class="sb-stat">
            <span class="sb-val configured">{{ configuredCount }}</span>
            <span class="sb-label">Configured</span>
          </div>
          <div class="sb-divider"></div>
          <div class="sb-stat">
            <span class="sb-val unconfigured">{{ table_datas.length - configuredCount }}</span>
            <span class="sb-label">Unconfigured</span>
          </div>
        </div>

        <!-- List column headers -->
        <div class="list-header">
          <div>
            <input class="row-checkbox" type="checkbox" v-model="selectAll" @change="selectAllServices()" />
          </div>
          <div>Service</div>
          <div style="text-align:right;">Default Rate</div>
          <div style="text-align:right;">Custom Rate</div>
          <div style="text-align:center;">Status</div>
          <div></div>
        </div>

        <!-- Service rows -->
        <div
          v-for="(data, index) in table_datas"
          :key="index"
          class="svc-row"
          :class="{ 'is-checked': data.checked, 'is-expanded': !!expandedRows[index] }"
        >
          <!-- Row header (click to expand) -->
          <div class="svc-row-head" @click="toggleExpand(index)">
            <div @click.stop>
              <input class="row-checkbox" type="checkbox" v-model="data.checked" />
            </div>

            <div class="srh-service">
              <div class="svc-icon"><i class="fa fa-fingerprint"></i></div>
              <span class="svc-name">{{ data.service }}</span>
            </div>

            <div :class="['srh-rate', data.service_code === 'fdle' ? 'na' : '']">
              {{ data.service_code !== 'fdle' ? ('$' + (data.default_rate || '—')) : 'N/A' }}
            </div>

            <div class="srh-rate">
              <span v-if="data.rate" class="custom-rate-val">${{ data.rate }}</span>
              <span v-else class="na">—</span>
            </div>

            <div class="srh-status">
              <span v-if="data.service_code === 'fdle'" class="status-badge s-fdle">FDLE</span>
              <span v-else-if="data.rate" class="status-badge s-custom">
                <i class="fa fa-check-circle"></i> Custom
              </span>
              <span v-else class="status-badge s-default">Default</span>
            </div>

            <div class="srh-toggle">
              <i class="fa fa-chevron-down expand-icon" :class="{ rotated: expandedRows[index] }"></i>
            </div>
          </div>

          <!-- Expanded detail panel -->
          <div class="svc-detail" v-if="expandedRows[index]">
            <div class="rate-compare">
              <div class="rc-header">
                <div class="rc-col">Field</div>
                <div><span class="rc-badge def"><i class="fa fa-lock"></i> Default</span></div>
                <div><span class="rc-badge cus"><i class="fa fa-edit"></i> Custom</span></div>
              </div>

              <!-- Rate -->
              <div class="rc-row" v-if="data.service_code !== 'fdle'">
                <div class="rc-field">Rate</div>
                <div><span class="rc-def-val">${{ data.default_rate || '—' }}</span></div>
                <div>
                  <div class="input-wrap">
                    <span class="input-prefix">$</span>
                    <input class="rate-input" type="number" min="0" v-model="data.rate" placeholder="0.00" />
                  </div>
                </div>
              </div>

              <!-- Expedited Rate -->
              <div class="rc-row" v-if="data.service_code === 'apostille'">
                <div class="rc-field">Expedited Rate</div>
                <div><span class="rc-def-val">${{ data.default_expedited_rate || '—' }}</span></div>
                <div>
                  <div class="input-wrap">
                    <span class="input-prefix">$</span>
                    <input class="rate-input" type="text" v-model="data.expedited_rate" placeholder="0.00" />
                  </div>
                </div>
              </div>

              <!-- Additional Rate -->
              <div class="rc-row" v-if="data.has_additional_rate && data.service_code !== 'fdle'">
                <div class="rc-field">Additional Rate</div>
                <div><span class="rc-def-val">${{ data.default_additional_rate || '—' }}</span></div>
                <div>
                  <div class="input-wrap">
                    <span class="input-prefix">$</span>
                    <input class="rate-input" type="text" v-model="data.additional_rate" placeholder="0.00" />
                  </div>
                </div>
              </div>

              <!-- Tax -->
              <div class="rc-row" v-if="data.service_code !== 'fdle'">
                <div class="rc-field">Tax</div>
                <div><span class="rc-def-val">{{ data.default_tax || '—' }}%</span></div>
                <div>
                  <div class="input-wrap">
                    <span class="input-prefix">%</span>
                    <input class="rate-input" type="number" min="0" v-model="data.tax_percentage" placeholder="0" />
                  </div>
                </div>
              </div>

              <!-- Commission -->
              <div class="rc-row" v-if="data.service_code !== 'fdle'">
                <div class="rc-field">Commission</div>
                <div><span class="rc-def-val">{{ data.default_commission || '—' }}%</span></div>
                <div>
                  <div class="input-wrap">
                    <span class="input-prefix">%</span>
                    <input class="rate-input" type="number" min="0" v-model="data.commission_percentage" placeholder="0" />
                  </div>
                </div>
              </div>

              <!-- Hide in LiveScan -->
              <div class="rc-row">
                <div class="rc-field">Hide in LiveScan</div>
                <div><span class="rc-def-val" style="color:#a0aab8;">No</span></div>
                <div>
                  <label class="toggle">
                    <input type="checkbox" v-model="data.hide_in_livescan" :true-value="1" :false-value="0" />
                    <span class="toggle-track"></span>
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>
      </template>

    </div>

    <!-- Floating save bar -->
    <div class="save-bar" v-if="selectedCount > 0 && !loadingData">
      <div class="save-bar__inner">
        <span class="save-bar__info">
          <strong>{{ selectedCount }}</strong>
          service{{ selectedCount !== 1 ? 's' : '' }} selected for update
        </span>
        <button
          class="btn-save"
          ref="kt_save_service"
          @click="update('S', 'kt_save_service')"
        >
          <i class="fa fa-check"></i> Update Rates
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import {
  FETCH_B_SERVICES,
  FETCH_B_LOCATION_RATE_LISTS,
  UPDATE_B_LOCATION_RATE_LISTS
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Locations", route: "client.location" },
        { id: 3, title: "Edit Rate", route: "" }
      ],
      table_datas: [],
      errors: [],
      loadingData: false,
      serviceError: "",
      selectAll: false,
      services: [],
      rates: [],
      client: [],
      expandedRows: {}
    };
  },
  computed: {
    ...mapGetters(["getBServices", "getBRateLists"]),
    selectedCount() {
      return this.table_datas.filter(s => s.checked).length;
    },
    configuredCount() {
      return this.table_datas.filter(s => s.checked).length;
    }
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getServiceData();
  },
  methods: {
    toggleExpand(index) {
      this.$set(this.expandedRows, index, !this.expandedRows[index]);
    },
    selectAllServices() {
      this.table_datas.forEach(s => { s.checked = this.selectAll; });
    },
    closeBtnLoad(ref) {
      const btn = this.$refs[ref];
      if (btn) btn.disabled = false;
    },
    loadingButton(ref) {
      const btn = this.$refs[ref];
      if (btn) btn.disabled = true;
    },
    getServiceData() {
      this.loadingData = true;
      this.$store.dispatch(FETCH_B_SERVICES, { status: true })
        .then(data_service => {
          this.$store.dispatch(FETCH_B_LOCATION_RATE_LISTS, this.$route.params.id)
            .then(data => {
              this.rates  = data.rates;
              this.client = data.client;
              this.table_datas = [];
              data_service.data.forEach(service => {
                const key = this.rates.findIndex(x => x.service.id === service.id);
                if (key === -1) {
                  this.table_datas.push({
                    id: service.id,
                    service: service.name,
                    rate: "",
                    has_additional_rate: service.has_additional_rate,
                    additional_rate: "",
                    tax_percentage: "",
                    commission_percentage: "",
                    checked: false,
                    default_rate: service.default_rate,
                    default_additional_rate: service.additional_rate,
                    default_tax: service.tax_percentage,
                    default_commission: service.commission_percentage,
                    hide_in_livescan: 0,
                    service_code: service.service_type_code,
                    default_expedited_rate: service.default_expedited_rate,
                    expedited_rate: ""
                  });
                } else {
                  this.table_datas.push({
                    id: service.id,
                    service: service.name,
                    rate: this.rates[key].rate,
                    has_additional_rate: this.rates[key].has_additional_rate,
                    additional_rate: this.rates[key].additional_rate,
                    tax_percentage: this.rates[key].tax_percentage,
                    commission_percentage: this.rates[key].commission_percentage,
                    checked: true,
                    default_rate: service.default_rate,
                    default_additional_rate: service.additional_rate,
                    default_tax: service.tax_percentage,
                    default_commission: service.commission_percentage,
                    hide_in_livescan: this.rates[key].hide_in_livescan,
                    service_code: service.service_type_code,
                    default_expedited_rate: service.default_expedited_rate,
                    expedited_rate: this.rates[key].expedited_rate
                  });
                }
              });
              this.loadingData = false;
            })
            .catch(() => { this.loadingData = false; });
        })
        .catch(() => { this.loadingData = false; });
    },
    update(saveType, ref) {
      const selected = this.table_datas.filter(s => s.checked);
      if (selected.length === 0) {
        this.$toastr.e("Please select at least one service");
        return;
      }
      this.errors = [];
      this.loadingButton(ref);
      this.$store.dispatch(UPDATE_B_LOCATION_RATE_LISTS, {
        location_id: this.$route.params.id,
        rate_list: selected
      })
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          if (saveType === "S") {
            this.$router.push({ name: "client.location" });
          }
        })
        .catch(err => {
          this.errors = err;
          this.selectAll = false;
          this.serviceError = "";
          this.table_datas = [];
          this.getServiceData();
          this.closeBtnLoad(ref);
        });
    }
  }
};
</script>
